import { useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { ICatchARideAvailability, isInnerItemEditingAtom } from "@ryde/shared";

const normalizeListFieldData = (
  watchedList: unknown,
  fieldName: string,
  setValue: UseFormSetValue<FieldValues>,
): ICatchARideAvailability[] => {
  if (watchedList && !Array.isArray(watchedList)) {
    /* guard for an invalid value coming from the backend */
    setValue(fieldName, []);
    return [];
  }

  return (watchedList as ICatchARideAvailability[] | undefined) ?? [];
};

/** List state of the template: the rows, row removal, and the "a row is being edited" flag that blocks Save. */
export const useCatchARideAvailabilityTemplate = ({
  name,
  watchedValue,
  setValue,
}: {
  name: string;
  watchedValue: unknown;
  setValue: UseFormSetValue<FieldValues>;
}) => {
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  const availabilityList = useMemo(
    () => normalizeListFieldData(watchedValue, name, setValue),
    [name, setValue, watchedValue],
  );

  const removeHandler = useEvent((index: number) => {
    setValue(
      name,
      availabilityList.filter((_, itemIndex) => itemIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    );
  });

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  return {
    availabilityList,
    removeHandler,
    isInnerItemEditing: editingCount > 0,
    handleEditStart,
    handleEditEnd,
  };
};
